import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bot, X, Send, ThumbsUp, ThumbsDown } from "lucide-react";

/**
 * Checkmate Buddy — LLM-powered support chat.
 *
 * Backed by the Emergent Universal LLM key (gpt-5.4-mini) via the
 * `/api/buddy/chat` endpoint. Session id is generated once per tab so
 * multi-turn context sticks. Falls back to a friendly message if the
 * upstream LLM fails or the user isn't logged in.
 */
export default function CheckmateBuddy() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`tab-${Math.random().toString(36).slice(2, 10)}`);
  const stepRef = useRef(null);
  const proactiveRef = useRef(false);
  const scrollRef = useRef(null);
  const nextId = useRef(1);
  const msg = (from, body) => ({ id: `m${nextId.current++}`, from, body });
  const [history, setHistory] = useState([
    { id: "m0", from: "buddy", body: "Hey! I'm Checkmate Buddy. Ask me anything about setup, the 2026 tax engine, funding rails, or your first payroll run — I'll point you to the exact screen." },
  ]);
  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, open]);

  // Proactive trigger — a blocked onboarding step opens Buddy with the exact
  // Execution Instruction needed to clear it.
  useEffect(() => {
    const onProactive = (e) => {
      const { step, title, instruction } = e.detail || {};
      stepRef.current = step ?? null;
      proactiveRef.current = true;
      setOpen(true);
      setHistory((h) => [...h, msg("buddy",
        `Step ${step} · ${title} is blocking payroll.\n\nExecution instruction:\n${instruction}\n\nWant me to walk you through it?`)]);
    };
    window.addEventListener("buddy:proactive", onProactive);
    return () => window.removeEventListener("buddy:proactive", onProactive);
  }, []);

  const rate = async (m, resolved) => {
    setHistory((h) => h.map((x) => (x.id === m.id ? { ...x, resolved } : x)));
    try { await api.post(`/buddy/pulse/${m.pulseId}/resolution`, { resolved }); }
    catch (err) { console.warn("could not record Buddy feedback", err); }
  };

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true);
    setHistory((h) => [...h, msg("you", q), msg("buddy", "…thinking")]);
    setText("");
    try {
      const { data } = await api.post("/buddy/chat", {
        message: q,
        session_id: sessionRef.current,
        step: stepRef.current,
        // Only the turn that immediately follows the auto-open counts as a
        // proactive unblock — later follow-ups are ordinary questions.
        proactive: proactiveRef.current,
      });
      proactiveRef.current = false;
      setHistory((h) => {
        const copy = [...h];
        copy[copy.length - 1] = { ...copy[copy.length - 1], body: data.reply, pulseId: data.pulse_id };
        return copy;
      });
    } catch (e) {
      setHistory((h) => {
        const copy = [...h]; copy[copy.length - 1] = { ...copy[copy.length - 1], body: "Sorry — I couldn't reach the assistant just now. Try again or email support@checkmatepayrolls.com." }; return copy;
      });
      toast.error(formatApiError(e));
    } finally {
      // Never leave the proactive flag armed — a failed turn is already logged.
      proactiveRef.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="buddy-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 border-2 border-ink bg-ink text-white p-4 rounded-full shadow-hard-lg hover:scale-105 transition"
          title="Ask Checkmate Buddy"
        ><Bot className="w-5 h-5" /></button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-96 max-w-[calc(100vw-3rem)] h-[500px] border-2 border-ink bg-white shadow-hard-lg flex flex-col" data-testid="buddy-window">
          <div className="border-b-2 border-ink px-4 py-3 flex items-center justify-between bg-ink text-white">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">§ Checkmate Buddy · AI</div>
                <div className="font-heading font-black text-sm">How can I help?</div>
              </div>
            </div>
            <button data-testid="buddy-close" onClick={() => setOpen(false)} className="p-1 hover:bg-white hover:text-ink"><X className="w-4 h-4" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 font-mono text-xs">
            {history.map((m) => (
              <div key={m.id} className={m.from === "you" ? "text-right" : ""}>
                <div className={"inline-block px-3 py-2 border-2 border-ink max-w-[85%] whitespace-pre-wrap " + (m.from === "you" ? "bg-secondary" : "bg-white")}>
                  {m.body}
                </div>
                {m.pulseId && (
                  <div className="flex items-center gap-2 mt-1" data-testid={`buddy-feedback-${m.id}`}>
                    {m.resolved === undefined ? (
                      <>
                        <span className="opacity-60">Did that help?</span>
                        <button data-testid={`buddy-thumbs-up-${m.id}`} onClick={() => rate(m, true)}
                          className="border border-ink px-1.5 py-0.5 hover:bg-ink hover:text-white" title="Yes">
                          <ThumbsUp className="w-3 h-3" />
                        </button>
                        <button data-testid={`buddy-thumbs-down-${m.id}`} onClick={() => rate(m, false)}
                          className="border border-ink px-1.5 py-0.5 hover:bg-ink hover:text-white" title="No">
                          <ThumbsDown className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <span className="opacity-60" data-testid={`buddy-rated-${m.id}`}>
                        {m.resolved ? "Marked resolved — thanks." : "Noted — I'll flag this step."}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t-2 border-ink p-3 flex gap-2">
            <input
              data-testid="buddy-input"
              value={text}
              disabled={busy}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={busy ? "Thinking…" : "How do I…?"}
              className="flex-1 border-2 border-ink px-2 py-1 font-mono text-xs disabled:opacity-60"
            />
            <button data-testid="buddy-send" disabled={busy || !text.trim()} onClick={send} className="hard-btn px-3 py-1 disabled:opacity-40"><Send className="w-3 h-3" /></button>
          </div>
        </div>
      )}
    </>
  );
}
